import { parseISO } from 'date-fns';

import type { SchedulingSlot, SchedulingSlotDateTime } from '@/types/scheduling.type';

/**
 * Merges two scheduling responses by calendar day: priority slots first, then supplemental.
 * Duplicate `start` ISO timestamps keep the first (priority) entry. Slots are sorted by start time.
 */
export function mergeSchedulingSlotsByDate(
  prioritySlots: SchedulingSlot[] | undefined,
  supplementalSlots: SchedulingSlot[] | undefined
): SchedulingSlot[] {
  const priority = prioritySlots ?? [];
  const supplemental = supplementalSlots ?? [];

  const dateSet = new Set<string>();
  for (const row of priority) {
    dateSet.add(row.date);
  }
  for (const row of supplemental) {
    dateSet.add(row.date);
  }

  const sortedDates = [...dateSet].sort((a, b) => a.localeCompare(b));

  const priorityByDate = new Map(priority.map((row) => [row.date, row.slots]));
  const supplementalByDate = new Map(supplemental.map((row) => [row.date, row.slots]));

  return sortedDates.map((date) => ({
    date,
    slots: mergeSlotsForSingleDay(
      priorityByDate.get(date) ?? [],
      supplementalByDate.get(date) ?? []
    ),
  }));
}

function mergeSlotsForSingleDay(
  priority: SchedulingSlotDateTime[],
  supplemental: SchedulingSlotDateTime[]
): SchedulingSlotDateTime[] {
  const seen = new Set<string>();
  const result: SchedulingSlotDateTime[] = [];

  for (const slot of [...priority, ...supplemental]) {
    // Intentional: deduplication is by start timestamp only. If a priority and supplemental
    // slot share the same start time (regardless of appointmentTypeId), the priority entry wins
    // and the supplemental entry is dropped. We never want to show two slots at the same time.
    if (seen.has(slot.start)) {
      continue;
    }
    seen.add(slot.start);
    result.push(slot);
  }

  result.sort((a, b) => parseISO(a.start).getTime() - parseISO(b.start).getTime());

  return result;
}
